/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import { getContainerPadding, mergeWithCss } from "@monsma/XDL/Utilities";
import { zoneTitle } from "@monsma/XDL/Utilities/sharedStyles";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SharedStylesWithinFooter {
    sharedStyles1?: InjectableCss;
}

export const sharedStylesWithinFooter: SharedStylesWithinFooter = {
    sharedStyles1: {
        css: css`
            font-size: 11px;
            color: ${getColor("primary.contrast")};
            line-height: 15px;
        `,
    },
};

export interface FooterContainerStyles {
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    defaultGridContainer?: GridContainerProps;
    defaultGridItem?: GridItemProps;
    addressGridItem?: GridItemProps;
    links2GridItem?: GridItemProps;
    defaultTopSectionGridItem?: GridItemProps;
    topSectionGridItem1?: GridItemProps;
    topSectionGridItem2?: GridItemProps;
    topSectionGridItem3?: GridItemProps;
    bottomSectionGridItem?: GridItemProps;
    footerSeparatorGridItem?: GridItemProps;
    footerSeparator?: InjectableCss;
    copyRight?: InjectableCss;
    addressArea1Wrapper?: InjectableCss;
    addressArea2Wrapper?: InjectableCss;
    links1GridItem1Top?: GridItemProps;
    linksTopWrapper?: InjectableCss;
    linksBottomWrapper?: InjectableCss;
    topSectionLinksArea?: InjectableCss;
    footerLinksSeparator?: InjectableCss;
    footerLink?: InjectableCss;
    footerHeadTitle?: InjectableCss;
    mainGridContainer?: InjectableCss;
    links1GridItem?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    topSection: {
        css: css`
            padding: 114px 0 60px;

            ${screen("lte", "sm")} {
                padding: 223px 0 60px;
            }

            ${screen("only", "md")} {
                padding: 126px 0 60px;
            }

            a {
                &,
                * {
                    line-height: 14px;
                }
            }
        `,
    },
    bottomSection: {
        css: css`
            padding: 20px 0;

            * {
                line-height: 12px;
            }
        `,
    },
    defaultGridContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    defaultGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    addressGridItem: {
        width: [6, 6, 12, 6, 6],
        css: css`
            ${screen("only", "md")} {
                margin-bottom: 40px !important;

                &:last-child {
                    margin-bottom: 0 !important;
                }
            }
        `,
    },
    links2GridItem: {
        width: [6, 6, 12, 6, 6],
    },
    defaultTopSectionGridItem: {
        css: css`
            ${screen("lte", "sm")} {
                margin-bottom: 40px !important;

                &:last-child {
                    margin-bottom: 0 !important;
                }
            }
        `,
    },
    topSectionGridItem1: {
        width: [12, 12, 3, 4, 4],
    },
    topSectionGridItem2: {
        width: [12, 12, 6, 4, 4],
    },
    topSectionGridItem3: {
        width: [12, 12, 3, 4, 4],
    },
    bottomSectionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;

            > div > div > div {
              padding: 0;
              text-transform: unset;
              
              > div {
                display: inline-flex;
                padding-right: 0;

                &:after {
                  content: " ";
                  display: inline-block;
                  font-size: 0;
                  width: 1px;
                  height: 14px;
                  background: ${getColor("primary.contrast")};
                  margin: 0 23px;
                }

                &:last-child {
                  &:after {
                    display: none;
                  }
                }
              }
            }

            > div > div {
                ${screen("lte", "sm")} {
                    text-align: center;
                }
            }
        `,
    },
    footerSeparatorGridItem: {
        width: 12,
    },
    footerSeparator: {
        css: css`
            height: 1px;
            width: 100%;
            background: ${getColor("common.black")};
        `,
    },
    copyRight: {
        css: css`
            ${sharedStylesWithinFooter.sharedStyles1?.css};
            width: 100%;

            ${screen("lte", "sm")} {
                text-align: center;
                margin-bottom: 30px;
            }
        `,
    },
    addressArea1Wrapper: {
        css: css`
            line-height: 0.8;
            padding-right: 31px;

            ${screen("lte", "sm")} {
                padding-right: 20px;
            }

            p {
                ${sharedStylesWithinFooter.sharedStyles1?.css};
                margin-bottom: 15px;
            }
        `,
    },
    addressArea2Wrapper: {
        css: css`
            line-height: 0.8;

            ${screen("only", "md")} {
                padding-right: 35px;
            }

            > div > div > div {
                > a {
                    margin-bottom: 15px;
                }

                > div {
                    margin-bottom: 15px;

                    > p {
                        ${sharedStylesWithinFooter.sharedStyles1?.css};
                        text-transform: uppercase;
                        margin-bottom: 0;
                    }

                    > a {
                        + p {
                            margin-top: 5px;
                            text-transform: none;
                        }
                    }

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }
        `,
    },
    linksTopWrapper: {
        css: css`
            width: 100%;
        `,
    },
    linksBottomWrapper: {
        css: css`
            width: 100%;

            > div > div > div {
                &:first-child {
                    text-transform: uppercase;
                    ${sharedStylesWithinFooter.sharedStyles1?.css};
                }
            }
        `,
    },
    topSectionLinksArea: {
        css: css`
            * {
                line-height: 12px;
            }

            > div > div {
              padding: 0;
              text-transform: unset;
              
              > div {
                margin-bottom: 10px;
                padding: 0;

                &:last-child {
                  margin-bottom: 0;
                }

                &:first-child {
                  * {
                    font-size: 11px !important;
                  }
                }
              }
            }
        `,
    },
    footerLinksSeparator: {
        css: css`
            margin: 21.5px 0;
        `,
    },
    footerLink: {
        css: css`
            a {
                span {
                    font-size: 11px;

                    &,
                    &:focus,
                    &:active,
                    &:hover {
                        color: ${getColor("common.ui5")} !important;
                    }

                    &:hover {
                        text-decoration: underline;
                    }
                }
            }
        `,
    },
    footerHeadTitle: {
        css: css`
            h6 {
                color: ${getColor("primary.contrast")};
                font-size: 13px;
                line-height: 17px;
                text-transform: uppercase;
                font-weight: 600;
                margin-bottom: 15px !important;

                span {
                    font-weight: 200;
                    display: block;
                }
            }
        `,
    },
    mainGridContainer: {
        css: css`
            ${getContainerPadding()};
        `,
    },
    links1GridItem: {
        css: css`
            padding-right: 33px;

            ${screen("lte", "sm")} {
                padding-right: 10px;
            }
        `,
    },
};

const FooterContainer: FC<Props> = ({ id, extendedStyles, pageType }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    const isFooterPage = pageType === "Footer";
    const showZoneTitle = isCmsEditing && isFooterPage;

    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <>
            {!isItCheckoutPage(pageType) && (
                <>
                    <StyledWrapper {...mergeWithCss(styles.topSection, styles.footerLink)}>
                        <GridContainer {...mergeWithCss(styles.defaultGridContainer, styles.mainGridContainer)}>
                            <GridItem {...mergeWithCss(styles.defaultTopSectionGridItem, styles.topSectionGridItem1)}>
                                <GridContainer {...styles.defaultGridContainer}>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.addressGridItem)}>
                                        <StyledWrapper
                                            {...mergeWithCss(styles.addressArea1Wrapper, styles.footerHeadTitle)}
                                        >
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Address area1 Zone</Typography>
                                            )}
                                            <Zone zoneName="AddressArea1" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.addressGridItem)}>
                                        <StyledWrapper {...styles.addressArea2Wrapper}>
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Address area2 Zone</Typography>
                                            )}
                                            <Zone zoneName="AddressArea2" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...mergeWithCss(styles.defaultTopSectionGridItem, styles.topSectionGridItem2)}>
                                <GridContainer {...styles.defaultGridContainer}>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.links1GridItem)}>
                                        <StyledWrapper {...styles.linksTopWrapper}>
                                            <StyledWrapper {...styles.footerHeadTitle}>
                                                {showZoneTitle && (
                                                    <Typography {...zoneTitle}>Top Links1 Title Zone</Typography>
                                                )}
                                                <Zone zoneName="TopLinksTitle1" contentId={id} />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.topSectionLinksArea}>
                                                {showZoneTitle && (
                                                    <Typography {...zoneTitle}>Top Links1 Zone</Typography>
                                                )}
                                                <Zone zoneName="TopLinks1" contentId={id} />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...mergeWithCss(styles.footerSeparator, styles.footerLinksSeparator)}
                                        />
                                        <StyledWrapper
                                            {...mergeWithCss(styles.linksBottomWrapper, styles.topSectionLinksArea)}
                                        >
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Bottom Links1 Zone</Typography>
                                            )}
                                            <Zone zoneName="BottomLinks1" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.links1GridItem)}>
                                        <StyledWrapper {...styles.linksTopWrapper}>
                                            <StyledWrapper {...styles.footerHeadTitle}>
                                                {showZoneTitle && (
                                                    <Typography {...zoneTitle}>Top Links2 Title Zone</Typography>
                                                )}
                                                <Zone zoneName="TopLinksTitle2" contentId={id} />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.topSectionLinksArea}>
                                                {showZoneTitle && (
                                                    <Typography {...zoneTitle}>Top Links2 Zone</Typography>
                                                )}
                                                <Zone zoneName="TopLinks2" contentId={id} />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...mergeWithCss(styles.footerSeparator, styles.footerLinksSeparator)}
                                        />
                                        <StyledWrapper
                                            {...mergeWithCss(styles.linksBottomWrapper, styles.topSectionLinksArea)}
                                        >
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Bottom Links2 Zone</Typography>
                                            )}
                                            <Zone zoneName="BottomLinks2" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...mergeWithCss(styles.defaultTopSectionGridItem, styles.topSectionGridItem3)}>
                                <GridContainer {...styles.defaultGridContainer}>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.addressGridItem)}>
                                        <StyledWrapper {...styles.footerHeadTitle}>
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Top Links3 Title Zone</Typography>
                                            )}
                                            <Zone zoneName="TopLinksTitle3" contentId={id} />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.topSectionLinksArea}>
                                            {showZoneTitle && <Typography {...zoneTitle}>Top Links3 Zone</Typography>}
                                            <Zone zoneName="TopLinks3" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...mergeWithCss(styles.defaultGridItem, styles.addressGridItem)}>
                                        <StyledWrapper {...styles.footerHeadTitle}>
                                            {showZoneTitle && (
                                                <Typography {...zoneTitle}>Top Links4 Title Zone</Typography>
                                            )}
                                            <Zone zoneName="TopLinksTitle4" contentId={id} />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.topSectionLinksArea}>
                                            {showZoneTitle && <Typography {...zoneTitle}>Top Links4 Zone</Typography>}
                                            <Zone zoneName="TopLinks4" contentId={id} />
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                    <StyledWrapper>
                        <GridContainer {...mergeWithCss(styles.defaultGridContainer, styles.mainGridContainer)}>
                            <GridItem {...styles.footerSeparatorGridItem}>
                                <StyledWrapper {...styles.footerSeparator} />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </>
            )}
            <StyledWrapper {...mergeWithCss(styles.bottomSection, styles.footerLink)}>
                <GridContainer {...mergeWithCss(styles.defaultGridContainer, styles.mainGridContainer)}>
                    <GridItem {...styles.bottomSectionGridItem}>
                        <StyledWrapper {...styles.copyRight}>{copyrightMessage()}</StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.bottomSectionGridItem}>
                        {showZoneTitle && <Typography {...zoneTitle}>Terms & Privacy Zone</Typography>}
                        <Zone zoneName="TermsPrivacy" contentId={id} />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps, null)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
